import { getLocalTimeZone } from '@internationalized/date'
import { ChevronRight, MarkerPin01, Scan, UserCircle } from '@untitledui/icons'
import { useQueryState } from 'nuqs'
import { type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import Flag from '@components/Flag.tsx'
import { Avatar } from '@components/designSystem/Avatar'
import { useCurrentStore } from '@hooks/useCurrentStore.ts'
import { useScan } from '@hooks/useScan.ts'
import { LATEST_DAY, useDayLink } from '@hooks/useSelectedDay'
import useTypedTranslation from '@hooks/useTypedTranslation'
import DictionaryAttribution from '@routes/Account/DictionaryAttribution'
import SignOutButton from '@routes/Account/SignOutButton'
import { useScansOfCurrentStore, useStoreDays } from '@state/scans.ts'
import { useStoreGrants } from '@state/store.ts'
import { useCurrentUser } from '@state/user.ts'
import { VERSION } from '@state/version.ts'
import { useViewSettings } from '@state/viewSettings'
import { formatRelativeDate } from '@utils/dates'
import { calculateStoreDays } from '@utils/storeDays.ts'

import ubicaFooter from './ubica_footer.png'

function AccountRouteMobile() {
  const { t } = useTypedTranslation()
  const { fullname, email } = useCurrentUser()

  return (
    <div className='flex size-full flex-col items-center overflow-y-auto p-1'>
      <div className='my-auto flex w-full flex-col items-center gap-5 py-4'>
        <div className='flex flex-col items-center gap-2 pb-5 text-center'>
          <Avatar size='3xl' />
          <p className='text-xl font-medium'>{fullname}</p>
          <p className='font-medium text-secondary'>{email}</p>
        </div>
        <section className='flex w-full flex-col gap-4'>
          <StoreCard />
          <DateCard />
        </section>
        <section className='flex w-full flex-col gap-2'>
          <h3 className='pl-1 font-medium text-secondary'>
            {t('account.systemSettings')}
          </h3>
          <LanguageCard />
        </section>
        <div className='mt-3'>
          <SignOutButton />
        </div>
        <div className='flex flex-col items-center gap-2'>
          <img className='w-28' src={ubicaFooter} alt='Ubica Logo' />
          <small className='text-sm text-secondary'>
            {t('account.version.label')}: <code>{VERSION}</code>
          </small>
          <DictionaryAttribution className='text-center text-xs text-tertiary' />
        </div>
      </div>
    </div>
  )
}

function StoreCard() {
  const { t } = useTypedTranslation()
  const { data: storeGrants } = useStoreGrants()
  const { name, address, role } = useCurrentStore()

  const enableStoreSwitch = storeGrants.length > 1

  return (
    <Card
      icon={<MarkerPin01 />}
      label={t('common.store')}
      value={
        <>
          <p>{name}</p>
          <p className='text-secondary'>
            {address ? `${address.addressLines[0]}, ${address.city}` : t('common.noAddress')}
          </p>
        </>
      }
      meta={
        <div className='flex items-center justify-between font-medium text-secondary'>
          <div className='flex items-center gap-3'>
            <UserCircle />
            <p>{t('common.role')}</p>
          </div>
          <p>{t(`common.roles.store.${role}`)}</p>
        </div>
      }
      href={enableStoreSwitch ? 'change-store' : undefined}
    />
  )
}

function DateCard() {
  const { isLatest } = useScan()
  const { data: settings } = useViewSettings()

  return settings?.dayViewIsDefault && isLatest ? <DayCard /> : <ScanCard />
}

function DayCard() {
  const { t, i18n } = useTypedTranslation()
  const { scanDate } = useScan()
  const { data: scans } = useScansOfCurrentStore()
  const { data: days = [] } = useStoreDays()
  const [dayParam] = useQueryState('day')
  const dayLink = useDayLink()

  const { currentDay } = calculateStoreDays(days, dayParam ?? LATEST_DAY)
  const formattedDate = formatRelativeDate(currentDay?.toDate(getLocalTimeZone()) ?? scanDate, i18n.resolvedLanguage)

  return (
    <Card
      icon={<Scan />}
      label={t('account.scan.label')}
      value={formattedDate}
      href={scans && scans.length > 1 ? dayLink('change-scan') : undefined}
    />
  )
}

function ScanCard() {
  const { t, i18n } = useTypedTranslation()
  const { scanDate } = useScan()
  const { data: scans } = useScansOfCurrentStore()

  const formattedDate = formatRelativeDate(scanDate, i18n.resolvedLanguage)

  return (
    <Card
      icon={<Scan />}
      label={t('account.scan.label')}
      value={formattedDate}
      href={scans && scans.length > 1 ? 'change-scan' : undefined}
    />
  )
}

function LanguageCard() {
  const { t, i18n } = useTypedTranslation()
  const lang = i18n.resolvedLanguage as 'de' | 'en'

  const values = {
    de: t('account.language.options.de'),
    en: t('account.language.options.en'),
  }

  return (
    <Card
      icon={<Flag lang={lang} />}
      label={t('account.language.label')}
      value={values[lang]}
      href="change-language"
    />
  )
}

interface CardProps {
    icon: ReactNode
    label: ReactNode
    value: ReactNode
    meta?: ReactNode
    href?: string
}

function Card({ meta, icon, label, value, href }: CardProps) {
  const location = useLocation()

  const content = (
    <>
      <div className={twJoin('flex items-center gap-2', href ? 'p-5 pr-1.5' : 'p-5')}>
        <div className='flex flex-1 items-center justify-between gap-2'>
          <div className='flex items-center gap-3 font-medium text-secondary'>
            {icon}
            <p>{label}</p>
          </div>
          <div className='text-right font-medium'>
            {value}
          </div>
        </div>
        {href && <ChevronRight className="text-black"/>}
      </div>
      {meta && (
        <div className='bg-quaternary p-5'>
          {meta}
        </div>
      )
      }
    </>
  )

  const containerClassNames = 'w-full rounded-md border border-secondary bg-primary shadow-sm'

  if (!href) {
    return (
      <div className={containerClassNames}>
        {content}
      </div>
    )
  }

  return (
    <Link className={containerClassNames} to={href} state={{ from: location.pathname }}>
      {content}
    </Link>
  )
}

export default AccountRouteMobile